import * as React from "react"
import { cn } from "@verlo/shared/lib/utils"
import { tone } from "@verlo/shared/lib/tones"

/**
 * An inline notice: the thing that used to be an amber box.
 *
 * A white surface with a hairline and a 3px rail in the tone's line colour; a one-word
 * category in the tone's ink; a bold first line; the detail under it; at most one action
 * on the right. No fill, no icon. Sits in the flow where the thing it's about is — the
 * rota notice on Service, the conflict notice in the booking dialog, the sync notice in
 * the back office — never floats (that's a toast).
 *
 *   <Notice tone="warn" word="Rota" title="4 due on shift, not clocked in" action={<Button …>Open rota</Button>}>
 *     John Smith, Sarah Jones, Lisa Davis +1
 *   </Notice>
 *
 * `word` is what the notice is about, one word, and is what the eye lands on first — make
 * it the noun (Rota, Allergy, Sync, Booking), not the tone (Warning).
 */
const Notice = React.forwardRef(({ tone: name = "info", word, title, action, className, children, ...props }, ref) => {
  const t = tone(name)
  return (
    <div
      ref={ref}
      role={name === "stop" ? "alert" : "status"}
      data-tone={name}
      className={cn(
        "flex items-start gap-3 border border-hairline border-l-[3px] bg-white px-3.5 py-2.5 text-[13px] leading-snug text-ink",
        t.rail,
        className,
      )}
      {...props}
    >
      {word && (
        <span className={cn("min-w-[3rem] pt-[3px] font-heading text-[10.5px] font-bold uppercase leading-none tracking-[0.06em]", t.word)}>
          {word}
        </span>
      )}
      <div className="min-w-0 flex-1">
        {title && <div className="font-heading font-bold">{title}</div>}
        {children && <div className={cn("text-ink-soft", title && "mt-0.5")}>{children}</div>}
      </div>
      {action && <div className="ml-auto shrink-0 self-center">{action}</div>}
    </div>
  )
})
Notice.displayName = "Notice"

export { Notice }
